import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import useSearchFlights from '../useSearchFlights'
import { fareCabinLabelMap, FlightsClassTypes } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useContext } from 'react'
import { FareFamiliesResponse, getFlightFareFamilies } from 'api/flights'
import GeoContext from 'contexts/geoContext'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyObject } from 'lib/object/objectUtils'
import { getDayOfWeekAtLeastOneWeekAway } from 'lib/flights/charterFlightUtils'

export function extractDepartingAndReturningFaresForClass(fareFamilies: FareFamiliesResponse, fareClass: FlightsClassTypes): [App.FlightFareFamily | undefined, App.FlightFareFamily | undefined] {
  const fareCabinLabel = fareCabinLabelMap[fareClass]
  const departingFareFamily = fareFamilies.upsellFaresPerLeg?.[0]?.find(upsell => upsell.cabin === fareCabinLabel)
  const returningFareFamily = fareFamilies.upsellFaresPerLeg?.[1]?.find(upsell => upsell.cabin === fareCabinLabel)
  return [departingFareFamily, returningFareFamily]
}

export default function useCharterFlightJourneys({
  charterBundleRoute,
  departDate,
  returnDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  departDate?: string
  returnDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
  disabled?: boolean
}) {
  const routeInfo = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined
  const { currentRegionCode } = useContext(GeoContext)

  const defaultDepartureDate = routeInfo ?
      getDayOfWeekAtLeastOneWeekAway(routeInfo.departureDays[0]) :
      moment() // Search will be disabled when there's no route info, so this fallback won't be used
  const defaultDuration = routeInfo?.validDurations[0] ?? 7

  // GET FLIGHTS
  const flightSearchArgs: Parameters<typeof useSearchFlights>[0] = {
    itinerary: [{
      departureAirport: routeInfo?.originAirport.airportCode ?? '',
      arrivalAirport: routeInfo?.destinationAirport.airportCode ?? '',
      departureDate: departDate ?? defaultDepartureDate.format(ISO_DATE_FORMAT),
    },
    {
      departureAirport: routeInfo?.destinationAirport.airportCode ?? '',
      arrivalAirport: routeInfo?.originAirport.airportCode ?? '',
      departureDate: returnDate ?? defaultDepartureDate.add(defaultDuration, 'day').format(ISO_DATE_FORMAT),
    }],
    occupancies,
    fareClass,
    source: 'charter',
  }

  const {
    journeys: departingJourneys,
    fetching: fetchingJourneys,
    error: journeysError,
    searchResultsTimestamp,
    providerSearchId,
  } = useSearchFlights(flightSearchArgs, {
    disabled,
    activeSegmentKey: 'departing',
    segments: ['departing', 'returning'],
  })

  const departingJourney = departingJourneys.at(0)

  const {
    journeys: returningJourneys,
  } = useSearchFlights(flightSearchArgs, {
    // Always disable for the return flights, because it would just do the same search again.
    // It will still return the relevant data (filtered for returning flights) that was fetched
    // by the previous hook call.
    disabled: true,
    activeSegmentKey: 'returning',
    segments: ['departing', 'returning'],
    selectedFlights: departingJourney ? { departing: departingJourney } : undefined,
  })

  // Returning journey is the last journey in the returningJourneys array
  const returningJourney = returningJourneys.at(-1)

  // GET FARE FAMILIES
  const {
    data: { fareFamilies, departingFareFamily, returningFareFamily } = EmptyObject as { fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined },
    status: fareFamiliesFetchStatus,
  } = useQuery<{ fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined }>({
    queryFn: async() => {
      if (!providerSearchId || !departingJourney || !returningJourney) {
        return EmptyObject
      }

      const fareFamilies = await getFlightFareFamilies({
        searchId: providerSearchId,
        sessionId: '',
        origin: departingJourney.flightGroup.flights[0].departureAirport,
        destination: returningJourney.flightGroup.flights[0].departureAirport,
        occupancies: mergeOccupants(occupancies),
        region: currentRegionCode,
        isStandalone: false,
        selectedFareIds: [departingJourney.id, returningJourney.id],
      })

      const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)

      return { fareFamilies, departingFareFamily, returningFareFamily }
    },
    queryKey: [departingJourney?.flightGroup.flights[0].departureAirport, returningJourney?.flightGroup.flights[0].departureAirport, departingJourney?.id, returningJourney?.id, occupancies],
    enabled: !!(providerSearchId && departingJourney && returningJourney),
  })

  // If journey search is complete but no journeys found, we should not be fetching
  const shouldBeFetching = !!charterBundleRoute && (
    fetchingJourneys ||
    (fareFamiliesFetchStatus === 'loading' && !!departingJourney && !!returningJourney)
  )

  return {
    departingJourney,
    returningJourney,
    fetching: shouldBeFetching,
    error: !!charterBundleRoute && (!!journeysError || fareFamiliesFetchStatus === 'error'),
    searchResultsTimestamp,
    providerSearchId,
    departingFareFamily,
    returningFareFamily,
    fareFamilies,
  }
}
